import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Button,
  HStack,
  Text,
  Flex,
  Icon,
} from '@chakra-ui/react';
import {
  BreadcrumbRoot,
  BreadcrumbLink,
  BreadcrumbCurrentLink,
} from '@/components/ui/breadcrumb';
import { toaster } from '@/components/ui/toaster';
import { FiCheck, FiChevronRight, FiPlus } from 'react-icons/fi';
import { useOrgLocation } from '@/context/organization-location-context';
import { useDisclosure } from '@/hooks/use-disclosure';

import { Organization } from '@/features/organizations/organizations.types';
import { OrganizationList } from '@/features/organizations/components/organization-list';
import OrganisationCreateModal from '@/features/organizations/components/organisation-create-modal';
import { OrganizationDetailsDrawer } from '@/features/organizations/components/organization-details-drawer';
import { dataTableButtonStyles } from '@/components/data-table';

const OrganisationSelectionPage: React.FC = () => {
  const { selectOrganisation, selectedOrganisation, organisationError } =
    useOrgLocation();

  const navigate = useNavigate();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [viewOrganizationId, setViewOrganizationId] = useState<string | null>(
    null,
  );

  const {
    open: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();

  // Handle organization selection
  const handleSelectOrganisation = (org: Organization) => {
    selectOrganisation({ id: org.id, name: org.name });
  };

  // Handle continue button click
  const handleContinue = () => {
    if (selectedOrganisation) {
      navigate('/location-selection', { replace: true });
    } else {
      toaster.create({
        title: 'No organization selected',
        description: 'Please select an organization to continue',
        type: 'warning',
        duration: 3000,
      });
    }
  };

  // Handle successful organization creation
  const handleOrganisationCreated = (newOrg: Organization) => {
    // Select the newly created organization
    handleSelectOrganisation(newOrg as Organization);

    toaster.create({
      title: 'Organization created',
      description: `${newOrg.name} has been created and selected`,
      type: 'success',
      duration: 3000,
    });
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/organisation-selection');
  };

  // Handle view organization
  const handleViewOrganization = (org: Organization) => {
    setViewOrganizationId(org.id);
    onDrawerOpen();
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setViewOrganizationId(null);
    onDrawerClose();
  };

  return (
    <Box w="full" h="100%" display="flex" flexDirection="column">
      <Box flexShrink={0} p={6} pb={0}>
        {/* Breadcrumb navigation */}
        <BreadcrumbRoot
          separator={<Icon as={FiChevronRight} color="gray.500" />}
          mb={4}
          fontSize="sm"
        >
          <BreadcrumbLink onClick={handleBack} color="blue.500">
            Organization: {selectedOrganisation?.name}
          </BreadcrumbLink>
          <BreadcrumbCurrentLink>Select Organization</BreadcrumbCurrentLink>
        </BreadcrumbRoot>
        <Heading mb={6}>Select Organization</Heading>

        <Text mb={4} color="gray.600">
          {selectedOrganisation
            ? 'You can select a different organization if needed.'
            : 'Please select an organization to continue. You will only see organizations that you have access to.'}
        </Text>

        {/* Actions row */}
        <Flex mb={4} justifyContent="flex-end" alignItems="center">
          <HStack gap={3}>
            <Button
              {...dataTableButtonStyles.outline}
              onClick={() => setIsCreateModalOpen(true)}
            >
              <FiPlus />
              Create Organization
            </Button>
            <Button
              {...dataTableButtonStyles.solid}
              disabled={!selectedOrganisation}
              onClick={handleContinue}
            >
              <FiCheck />
              Continue
            </Button>
          </HStack>
        </Flex>

        {/* Error message */}
        {organisationError && (
          <Box
            bg="red.50"
            border="1px"
            borderColor="red.200"
            p={4}
            mb={4}
            borderRadius="md"
          >
            <Text color="red.600">
              Error loading organizations: {organisationError.message}
            </Text>
          </Box>
        )}

        <Box flex="1" minHeight={0} overflow="hidden" p={6} pt={4} h="100%">
          {/* Organization List */}
          <OrganizationList
            onSelect={handleSelectOrganisation}
            selectedOrganizationId={selectedOrganisation?.id}
            onView={handleViewOrganization}
          />
        </Box>

        {/* Organization Create Modal */}
        <Box flex="1" minHeight={0} overflow="hidden" pt={4} h="100%">
          <OrganisationCreateModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={handleOrganisationCreated}
          />
        </Box>

        {/* Organization Details Drawer */}
        <OrganizationDetailsDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          organizationId={viewOrganizationId}
        />
      </Box>
    </Box>
  );
};

export default OrganisationSelectionPage;
